import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { AssetClassification, ClassificationNode, ClassificationStatus } from "@/types/classifications";
import { logger } from "@/services/LoggingService";
import type { Json } from "@/integrations/supabase/types";
import { updateNodeInTree } from "../utils/nodeConverters";

export function useClassificationMutations(allClassifications?: AssetClassification[]) {
  const queryClient = useQueryClient();

  const { mutate: cloneClassification } = useMutation({
    mutationFn: async (classification: AssetClassification) => {
      const { data, error } = await supabase
        .from('asset_classifications')
        .insert({
          version: 1,
          effective_date: new Date().toISOString(),
          classification: classification.classification as unknown as Json,
          reference_name: `${classification.reference_name.split('_v')[0]}_clone_v1`,
          display_name: `${classification.display_name.split(' v')[0]} (Clone) v1`,
          provider_type: classification.provider_type,
          purpose: classification.purpose,
          status: 'draft' as ClassificationStatus,
          notes: `Cloned from ${classification.reference_name}`
        })
        .select()
        .single();

      if (error) {
        logger.error('classifications.clone', 'Failed to clone classification', {
          error: error.message,
          details: error,
          timestamp: new Date().toISOString()
        });
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      toast.success("Classification cloned successfully");
      queryClient.invalidateQueries({ queryKey: ['asset-classifications'] });
    },
    onError: (error: Error) => {
      toast.error("Failed to clone classification");
      logger.error('classifications.clone', 'Clone error', {
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
  });

  const { mutate: updateNode } = useMutation({
    mutationFn: async (params: { classificationId: string, node: ClassificationNode }) => {
      const classification = allClassifications?.find(c => c.id === params.classificationId);
      if (!classification) throw new Error("Classification not found");

      const updatedClassification = updateNodeInTree(
        classification.classification,
        params.node
      );

      const { data, error } = await supabase
        .from('asset_classifications')
        .update({ 
          classification: updatedClassification as unknown as Json,
          updated_at: new Date().toISOString()
        })
        .eq('id', params.classificationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ classificationId, node }) => {
      await queryClient.cancelQueries({ queryKey: ['asset-classifications'] });
      const previousData = queryClient.getQueryData(['asset-classifications']);

      queryClient.setQueryData(['asset-classifications'], (old: AssetClassification[] | undefined) => {
        if (!old) return [];
        return old.map(c => {
          if (c.id === classificationId) {
            return {
              ...c,
              classification: updateNodeInTree(c.classification, node)
            };
          }
          return c;
        });
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['asset-classifications'], context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-classifications'] });
    }
  });

  const { mutate: addChildNode } = useMutation({
    mutationFn: async (params: { 
      classificationId: string, 
      parentId: string, 
      newNode: Omit<ClassificationNode, 'id' | 'children'> 
    }) => {
      const classification = allClassifications?.find(c => c.id === params.classificationId);
      if (!classification) throw new Error("Classification not found");

      const newNode: ClassificationNode = {
        ...params.newNode,
        id: crypto.randomUUID(),
        children: []
      };

      const addNodeToParent = (tree: ClassificationNode): ClassificationNode => {
        if (tree.id === params.parentId) {
          return {
            ...tree,
            children: [...tree.children, newNode]
          };
        }
        return {
          ...tree,
          children: tree.children.map(child => addNodeToParent(child))
        };
      };

      const updatedClassification = addNodeToParent(classification.classification);

      const { data, error } = await supabase
        .from('asset_classifications')
        .update({ 
          classification: updatedClassification as unknown as Json,
          updated_at: new Date().toISOString()
        })
        .eq('id', params.classificationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Node added successfully");
      queryClient.invalidateQueries({ queryKey: ['asset-classifications'] });
    },
    onError: () => {
      toast.error("Failed to add node");
    }
  });

  const { mutate: removeNode } = useMutation({
    mutationFn: async (params: { classificationId: string, nodeId: string }) => {
      const classification = allClassifications?.find(c => c.id === params.classificationId);
      if (!classification) throw new Error("Classification not found");

      const removeNodeFromTree = (tree: ClassificationNode): ClassificationNode => {
        return {
          ...tree,
          children: tree.children
            .filter(child => child.id !== params.nodeId)
            .map(child => removeNodeFromTree(child))
        };
      };

      const updatedClassification = removeNodeFromTree(classification.classification);

      const { data, error } = await supabase
        .from('asset_classifications')
        .update({ 
          classification: updatedClassification as unknown as Json,
          updated_at: new Date().toISOString()
        })
        .eq('id', params.classificationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ classificationId, nodeId }) => {
      await queryClient.cancelQueries({ queryKey: ['asset-classifications'] });
      const previousData = queryClient.getQueryData(['asset-classifications']);

      const removeNodeFromTreeOptimistic = (tree: ClassificationNode): ClassificationNode => {
        return {
          ...tree,
          children: tree.children
            .filter(child => child.id !== nodeId)
            .map(child => removeNodeFromTreeOptimistic(child))
        };
      };

      queryClient.setQueryData(['asset-classifications'], (old: AssetClassification[] | undefined) => {
        if (!old) return [];
        return old.map(c => {
          if (c.id === classificationId) {
            return {
              ...c,
              classification: removeNodeFromTreeOptimistic(c.classification)
            };
          }
          return c;
        });
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['asset-classifications'], context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-classifications'] });
    }
  });

  const { mutate: deleteClassification } = useMutation({
    mutationFn: async (classificationId: string) => {
      const { error } = await supabase
        .from('asset_classifications')
        .delete()
        .eq('id', classificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Classification deleted successfully");
      queryClient.invalidateQueries({ queryKey: ['asset-classifications'] });
    },
    onError: () => {
      toast.error("Failed to delete classification");
    }
  });

  const { mutate: updateClassificationDetails } = useMutation({
    mutationFn: async (params: { 
      classificationId: string; 
      updates: Partial<Omit<AssetClassification, 'classification'>> & { 
        classification?: Json 
      }
    }) => {
      const { data, error } = await supabase
        .from('asset_classifications')
        .update({
          ...params.updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', params.classificationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Classification updated successfully");
      queryClient.invalidateQueries({ queryKey: ['asset-classifications'] });
    },
    onError: () => {
      toast.error("Failed to update classification");
    }
  });

  const { mutate: createClassification } = useMutation({
    mutationFn: async (newClassification: Omit<AssetClassification, 'id' | 'created_at' | 'updated_at' | 'version' | 'effective_date'>) => {
      const { data, error } = await supabase
        .from('asset_classifications')
        .insert({
          version: 1,
          effective_date: new Date().toISOString(),
          classification: newClassification.classification as unknown as Json,
          reference_name: newClassification.reference_name,
          display_name: newClassification.display_name,
          provider_type: newClassification.provider_type,
          purpose: newClassification.purpose,
          status: newClassification.status,
          notes: newClassification.notes
        })
        .select()
        .single();

      if (error) {
        logger.error('classifications.create', 'Failed to create classification', {
          error: error.message,
          details: error,
          timestamp: new Date().toISOString()
        });
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      toast.success("Classification created successfully");
      queryClient.invalidateQueries({ queryKey: ['asset-classifications'] });
    },
    onError: (error: Error) => {
      toast.error("Failed to create classification");
      logger.error('classifications.create', 'Creation error', {
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
  });

  return {
    cloneClassification,
    updateNode,
    addChildNode,
    removeNode,
    deleteClassification,
    updateClassificationDetails,
    createClassification
  };
}
